import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { formatMonthYearLt } from '../utils/ltDateFormat';

type CalendarTypeFilter = 'both' | 'edukacijos' | 'excursions';

interface AssignedColleague {
  firstName: string;
  lastName: string;
}

interface EdukationEvent {
  id: string;
  title: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  isActive: boolean;
  assignedColleague?: (AssignedColleague & { id?: string }) | null;
  assignedColleagues?: Array<AssignedColleague & { id?: string }>;
}

interface ExcursionEvent {
  id: string;
  title: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  isActive: boolean;
  assignedColleague?: AssignedColleague | null;
}

interface CalendarEvent {
  id: string;
  type: 'edukacija' | 'excursion';
  title: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
  assignedLabel?: string;
}

const excursionColor = '#7fff00';
const edukacijaColor = '#f0e3ff';

function edukationAssigneesLabel(e: EdukationEvent): string | undefined {
  if (e.assignedColleagues && e.assignedColleagues.length > 0) {
    return e.assignedColleagues.map((c) => `${c.firstName} ${c.lastName}`).join(', ');
  }
  if (e.assignedColleague) {
    return `${e.assignedColleague.firstName} ${e.assignedColleague.lastName}`;
  }
  return undefined;
}

interface ExhibitionCalendarProps {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
}

const ExhibitionCalendar: React.FC<ExhibitionCalendarProps> = ({
  currentMonth,
  onMonthChange
}) => {
  const { hasPermission } = useAuth();
  const canManageExcursions = hasPermission('excursions.manage');
  const canManageEdukacijos = hasPermission('edukacijos.manage');
  const todayYmd = format(new Date(), 'yyyy-MM-dd');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edukations, setEdukations] = useState<EdukationEvent[]>([]);
  const [excursions, setExcursions] = useState<ExcursionEvent[]>([]);
  const [typeFilter, setTypeFilter] = useState<CalendarTypeFilter>('both');
  const [selectedEdukacijaTitles, setSelectedEdukacijaTitles] = useState<string[]>([]);
  const [edukacijaFilterInitialized, setEdukacijaFilterInitialized] = useState(false);

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(monthEnd, { weekStartsOn: 1 })
    });
  }, [currentMonth]);

  const loadMonthData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const headers = { Authorization: `Bearer ${token || ''}` };

      const [edukRes, excRes] = await Promise.all([
        fetch(getApiUrl(`${API_ENDPOINTS.EDUKATIONS_BY_DATE_RANGE}?startDate=${startDate}&endDate=${endDate}`), { headers }),
        fetch(getApiUrl(`${API_ENDPOINTS.EXCURSIONS_BY_DATE_RANGE}?startDate=${startDate}&endDate=${endDate}`), { headers })
      ]);

      if (!edukRes.ok || !excRes.ok) {
        throw new Error('Failed to load calendar events');
      }

      const edukData = await edukRes.json();
      const excData = await excRes.json();
      setEdukations((edukData.data || []).filter((e: EdukationEvent) => e.isActive));
      setExcursions((excData.data || []).filter((e: ExcursionEvent) => e.isActive));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMonthData();
  }, [currentMonth]);

  const edukacijaCategories = useMemo(() => {
    return Array.from(
      new Set(edukations.filter((e) => e.status !== 'cancelled').map((e) => e.title).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, 'lt'));
  }, [edukations]);

  useEffect(() => {
    // On first load (or when categories change from empty to non-empty), default to all checked once.
    if (!edukacijaFilterInitialized && edukacijaCategories.length > 0) {
      setSelectedEdukacijaTitles(edukacijaCategories);
      setEdukacijaFilterInitialized(true);
    }
  }, [edukacijaCategories, edukacijaFilterInitialized]);

  const events = useMemo<CalendarEvent[]>(() => {
    const all: CalendarEvent[] = [];

    if (typeFilter === 'both' || typeFilter === 'edukacijos') {
      edukations
        .filter((e) => e.status !== 'cancelled')
        .filter((e) => selectedEdukacijaTitles.includes(e.title))
        .forEach((e) => {
          const assignedLabel = edukationAssigneesLabel(e);
          all.push({
            id: e.id,
            type: 'edukacija',
            title: e.title,
            eventDate: e.eventDate.split('T')[0],
            startTime: e.startTime,
            endTime: e.endTime,
            assignedLabel
          });
        });
    }

    if (typeFilter === 'both' || typeFilter === 'excursions') {
      excursions
        .filter((e) => e.status !== 'cancelled')
        .forEach((e) => {
          const assignedLabel = e.assignedColleague
            ? `${e.assignedColleague.firstName} ${e.assignedColleague.lastName}`
            : undefined;
          all.push({
            id: e.id,
            type: 'excursion',
            title: e.title,
            eventDate: e.eventDate.split('T')[0],
            startTime: e.startTime,
            endTime: e.endTime,
            status: e.status,
            assignedLabel
          });
        });
    }

    return all;
  }, [edukations, excursions, typeFilter, selectedEdukacijaTitles]);

  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((event) => {
      if (!map.has(event.eventDate)) {
        map.set(event.eventDate, []);
      }
      map.get(event.eventDate)!.push(event);
    });
    return map;
  }, [events]);

  const openCreateForDate = (date: string) => {
    if (typeFilter === 'excursions') {
      window.dispatchEvent(new CustomEvent('exhibition-calendar-open-excursion', { detail: { date } }));
      return;
    }
    if (typeFilter === 'edukacijos') {
      window.dispatchEvent(new CustomEvent('exhibition-calendar-open-edukation', { detail: { date } }));
      return;
    }
    // both: prefer edukacija if user can manage it; otherwise excursion
    if (canManageEdukacijos) {
      window.dispatchEvent(new CustomEvent('exhibition-calendar-open-edukation', { detail: { date } }));
    } else if (canManageExcursions) {
      window.dispatchEvent(new CustomEvent('exhibition-calendar-open-excursion', { detail: { date } }));
    }
  };

  const canShowAddForDay = (dayKey: string) => {
    if (dayKey < todayYmd) return false;
    if (typeFilter === 'excursions') return canManageExcursions;
    if (typeFilter === 'edukacijos') return canManageEdukacijos;
    return canManageExcursions || canManageEdukacijos;
  };

  const openEvent = (event: CalendarEvent) => {
    if (event.type === 'excursion') {
      const excursion = excursions.find((e) => e.id === event.id) || null;
      window.dispatchEvent(
        new CustomEvent('exhibition-calendar-open-excursion', {
          detail: { id: event.id, excursion }
        })
      );
      return;
    }
    const edukation = edukations.find((e) => e.id === event.id) || null;
    window.dispatchEvent(
      new CustomEvent('exhibition-calendar-open-edukation', {
        detail: { id: event.id, edukation }
      })
    );
  };

  useEffect(() => {
    const handler = () => {
      loadMonthData();
    };
    window.addEventListener('exhibition-calendar-refresh', handler);
    return () => {
      window.removeEventListener('exhibition-calendar-refresh', handler);
    };
  }, [currentMonth]);

  return (
    <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Kalendorius - Edukacijos ir Ekskursijos
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={typeFilter === 'both' ? 'contained' : 'outlined'}
              onClick={() => setTypeFilter('both')}
            >
              Abi
            </Button>
            <Button
              variant={typeFilter === 'edukacijos' ? 'contained' : 'outlined'}
              onClick={() => setTypeFilter('edukacijos')}
            >
              Edukacijos
            </Button>
            <Button
              variant={typeFilter === 'excursions' ? 'contained' : 'outlined'}
              onClick={() => setTypeFilter('excursions')}
            >
              Ekskursijos
            </Button>
          </ButtonGroup>

          {(typeFilter === 'both' || typeFilter === 'edukacijos') && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {edukacijaCategories.map((category) => (
                <FormControlLabel
                  key={category}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedEdukacijaTitles.includes(category)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedEdukacijaTitles((prev) => {
                          if (!checked) {
                            const next = prev.filter((c) => c !== category);
                            return next;
                          }
                          if (prev.includes(category)) return prev;
                          return [...prev, category];
                        });
                      }}
                    />
                  }
                  label={category}
                />
              ))}
            </Box>
          )}
        </Box>

        <Stack spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', textAlign: 'center' }}>
            {formatMonthYearLt(currentMonth)}
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => onMonthChange(startOfMonth(subMonths(currentMonth, 1)))}>
              Atgal
            </Button>
            <Button onClick={() => onMonthChange(startOfMonth(new Date()))}>Šis mėnuo</Button>
            <Button onClick={() => onMonthChange(startOfMonth(addMonths(currentMonth, 1)))}>
              Pirmyn
            </Button>
          </ButtonGroup>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <Typography color="text.secondary" sx={{ mb: 2 }}>Kraunama...</Typography>}

        <Paper variant="outlined">
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map((d) => (
              <Box key={d} sx={{ p: 1, borderBottom: 1, borderColor: 'divider', fontWeight: 600, textAlign: 'center' }}>{d}</Box>
            ))}
            {monthDays.map((date) => {
              const key = format(date, 'yyyy-MM-dd');
              const dayEvents = byDate.get(key) || [];
              return (
                <Box
                  key={key}
                  sx={{
                    minHeight: 120,
                    borderBottom: 1,
                    borderRight: 1,
                    borderColor: 'divider',
                    p: 1,
                    opacity: isSameMonth(date, currentMonth) ? 1 : 0.45
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{format(date, 'd')}</Typography>
                    <Tooltip
                      title={
                        key < todayYmd
                          ? 'Praeities dienoms sukurti negalima'
                          : !canShowAddForDay(key)
                            ? ''
                            : 'Pridėti'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          onClick={() => openCreateForDate(key)}
                          sx={{ minWidth: 'auto', px: 0.5 }}
                          disabled={!canShowAddForDay(key)}
                        >
                          +
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {dayEvents.slice(0, 4).map((event) => {
                      const unassignedExcursion =
                        event.type === 'excursion' &&
                        !event.assignedLabel &&
                        event.status === 'scheduled';
                      return (
                      <Button
                        key={`${event.type}-${event.id}`}
                        size="small"
                        onClick={() => openEvent(event)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          lineHeight: 1.2,
                          bgcolor: unassignedExcursion
                            ? 'rgba(255, 167, 38, 0.35)'
                            : event.type === 'excursion'
                              ? excursionColor
                              : edukacijaColor,
                          color: 'text.primary',
                          border: unassignedExcursion ? '2px solid' : '1px solid',
                          borderColor: unassignedExcursion ? 'warning.main' : 'divider',
                          boxShadow: unassignedExcursion
                            ? '0 0 0 1px rgba(255, 152, 0, 0.35)'
                            : 'none',
                          px: 0.75,
                          py: 0.4
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1.1 }}>
                            {event.startTime.slice(0, 5)} {event.title}
                          </Typography>
                          {event.assignedLabel ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}>
                              {event.assignedLabel}
                            </Typography>
                          ) : unassignedExcursion ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                lineHeight: 1.1,
                                fontWeight: 800,
                                color: 'warning.dark',
                                letterSpacing: '0.02em'
                              }}
                            >
                              Nepriskirta
                            </Typography>
                          ) : null}
                        </Box>
                      </Button>
                      );
                    })}
                    {dayEvents.length > 4 && (
                      <Typography variant="caption" color="text.secondary">
                        +{dayEvents.length - 4} daugiau
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
    </Box>
  );
};

export default ExhibitionCalendar;
